import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { ArrowLeft, CircleHelp, ClipboardList, History, Route } from "lucide-react";
import { Message } from "@arco-design/web-react";
import { Link, Navigate, Outlet, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { buildLegacyJumpUrl, useCurrentAuthUser } from "../../auth";
import { canUsePermission } from "../../../lib/auth";
import {
  buildCustomizationWorkbenchEntryPath,
  buildOpportunityAssociatedCustomizationReturnPath,
  isCustomizationMobileDevice,
} from "../../customization/runtime/customizationDevice";
import { parseLegacyJiDiaoRouteProject, readLegacyJiDiaoNoFromSearch } from "./legacyJiDiaoRoute";
import { useCustomizationMobileWorkbenchRuntime } from "./useCustomizationMobileWorkbenchRuntime";

const CUSTOMIZATION_PROJECT_LIST_PATH = "/crm/customization/projects";

type MobileWorkbenchPageKey = "workbench" | "traces" | "itinerary";
type MobileWorkbenchRuntime = ReturnType<typeof useCustomizationMobileWorkbenchRuntime>;
type MobileItinerarySsoResolution = {
  status: "loading" | "ready" | "error";
  editorUrl: string;
};

type CustomizationMobileWorkbenchSessionValue = {
  runtime: MobileWorkbenchRuntime;
  pendingTraceIds: string[];
  confirmedTraceIds: string[];
  expandedTraceIds: string[];
  itinerarySsoByDemandId: Record<string, MobileItinerarySsoResolution>;
  buildPagePath: (page: MobileWorkbenchPageKey) => string;
  confirmTraceSelection: () => void;
  resetPendingTraceSelection: () => void;
  resolveItinerarySso: (demandId: string, force?: boolean) => Promise<void>;
  toggleExpandedTrace: (traceId: string) => void;
  togglePendingTrace: (traceId: string) => void;
};

const CustomizationMobileWorkbenchSessionContext =
  createContext<CustomizationMobileWorkbenchSessionValue | null>(null);

/** 为手机需求、追溯和行程三页持有同一份工作台会话。 */
export function CustomizationMobileWorkbenchShell() {
  const params = useParams();
  const location = useLocation();
  const routeProjectId = params.projectId ?? "project-demo";
  const legacyRouteProject = parseLegacyJiDiaoRouteProject(routeProjectId);
  const resolvedProjectId = legacyRouteProject?.projectId ?? routeProjectId;
  if (resolvedProjectId === "new") {
    return (
      <Navigate
        to={`/crm/customization/projects/new/workbench${location.search}`}
        replace
      />
    );
  }
  // 取消手机视口（如关闭设备模拟）后刷新：mobile URL 恢复为 PC 工作台。
  if (!isCustomizationMobileDevice()) {
    return (
      <Navigate
        to={buildCustomizationWorkbenchEntryPath(resolvedProjectId, {
          search: location.search,
          forceMobile: false,
        })}
        replace
      />
    );
  }
  return <CustomizationMobileWorkbenchShellSession />;
}

/** 手机工作台三页共享会话与壳层布局。 */
function CustomizationMobileWorkbenchShellSession() {
  const currentUserQuery = useCurrentAuthUser();
  const shellRef = useRef<HTMLDivElement>(null);
  const params = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const routeProjectId = params.projectId ?? "project-demo";
  const legacyRouteProject = parseLegacyJiDiaoRouteProject(routeProjectId);
  const resolvedProjectId = legacyRouteProject?.projectId ?? routeProjectId;
  const initialLegacyJiDiaoNoRef = useRef<string | undefined>(undefined);
  const initialRouteRef = useRef("");
  if (initialRouteRef.current !== routeProjectId) {
    initialRouteRef.current = routeProjectId;
    const directPage = readMobileWorkbenchPageKey(location.pathname);
    initialLegacyJiDiaoNoRef.current =
      legacyRouteProject?.legacyJiDiaoNo ??
      (directPage === "workbench" ? undefined : readLegacyJiDiaoNoFromSearch(searchParams));
  }
  const runtime = useCustomizationMobileWorkbenchRuntime(resolvedProjectId, {
    canEditQuickItinerary: canUsePermission(currentUserQuery.data, "customization.project:quick_itinerary"),
    quickItineraryViewerId: currentUserQuery.data?.username,
    legacyJiDiaoNo: initialLegacyJiDiaoNoRef.current,
    onNewCustomizationCreated: (projectId) => {
      navigate(
        `/crm/customization-mobile/projects/${encodeURIComponent(projectId)}/workbench${location.search}`,
      );
    },
  });
  const confirmedTraceIds = runtime.confirmedTraceIds;
  const [pendingTraceIds, setPendingTraceIds] = useState<string[]>(confirmedTraceIds);
  const [expandedTraceIds, setExpandedTraceIds] = useState<string[]>([]);
  const [itinerarySsoByDemandId, setItinerarySsoByDemandId] = useState<
    Record<string, MobileItinerarySsoResolution>
  >({});
  const itinerarySsoRequestRef = useRef<Record<string, number>>({});
  const pageScrollOffsetsRef = useRef<Record<MobileWorkbenchPageKey, number>>({
    itinerary: 0,
    traces: 0,
    workbench: 0,
  });
  const currentPage = readMobileWorkbenchPageKey(location.pathname);
  const currentPageTitle = mobileWorkbenchPageTitles[currentPage];
  const previousPageRef = useRef<MobileWorkbenchPageKey | null>(null);
  const opportunityReturnPath = buildOpportunityAssociatedCustomizationReturnPath(searchParams);
  const workbenchBackPath = opportunityReturnPath ?? CUSTOMIZATION_PROJECT_LIST_PATH;
  const workbenchBackLabel = opportunityReturnPath
    ? searchParams.get("entry") === "add"
      ? "返回增定制"
      : "返回关联定制"
    : "返回定制项目列表";

  useLayoutEffect(() => {
    const scrollContainer =
      shellRef.current?.querySelector<HTMLElement>(".customization-mobile-page-scroll") ??
      shellRef.current?.querySelector<HTMLElement>(".customization-mobile-workbench-page") ??
      shellRef.current?.closest<HTMLElement>(".arco-layout-content");
    const targetOffset = pageScrollOffsetsRef.current[currentPage];
    const restoreScrollOffset = () => {
      if (scrollContainer) {
        scrollContainer.scrollTop = targetOffset;
      } else {
        window.scrollTo({
          behavior: "auto",
          left: 0,
          top: targetOffset,
        });
      }
    };
    restoreScrollOffset();
    const frameId = window.requestAnimationFrame(restoreScrollOffset);
    const recordScrollOffset = () => {
      pageScrollOffsetsRef.current[currentPage] = scrollContainer?.scrollTop ?? window.scrollY;
    };
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", recordScrollOffset, { passive: true });
    } else {
      window.addEventListener("scroll", recordScrollOffset, { passive: true });
    }
    return () => {
      window.cancelAnimationFrame(frameId);
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", recordScrollOffset);
      } else {
        window.removeEventListener("scroll", recordScrollOffset);
      }
    };
  }, [currentPage]);

  useEffect(() => {
    if (currentPage === "traces" && previousPageRef.current !== "traces") {
      setPendingTraceIds(confirmedTraceIds);
    }
    previousPageRef.current = currentPage;
  }, [confirmedTraceIds, currentPage]);

  const buildPagePath = (page: MobileWorkbenchPageKey) => {
    const query = location.search;
    return `/crm/customization-mobile/projects/${encodeURIComponent(routeProjectId)}/${page}${query}`;
  };
  const resolveItinerarySso = useCallback(async (demandId: string, force = false) => {
    const normalizedDemandId = demandId.trim();
    if (!normalizedDemandId) {
      return;
    }
    if (!force) {
      const current = itinerarySsoByDemandId[normalizedDemandId];
      if (current?.status === "loading" || current?.status === "ready") {
        return;
      }
    }
    const requestId = (itinerarySsoRequestRef.current[normalizedDemandId] ?? 0) + 1;
    itinerarySsoRequestRef.current[normalizedDemandId] = requestId;
    setItinerarySsoByDemandId((current) => ({
      ...current,
      [normalizedDemandId]: { status: "loading", editorUrl: "" },
    }));
    const targetUrl = `/chat_v2/route_plan/entry?demand_id=${encodeURIComponent(normalizedDemandId)}`;
    try {
      const result = await buildLegacyJumpUrl({ targetUrl });
      const editorUrl = buildTrustedLegacyItineraryUrl(result.jumpUrl);
      if (!editorUrl) {
        throw new Error("Invalid trusted itinerary URL");
      }
      if (itinerarySsoRequestRef.current[normalizedDemandId] !== requestId) {
        return;
      }
      setItinerarySsoByDemandId((current) => ({
        ...current,
        [normalizedDemandId]: { status: "ready", editorUrl },
      }));
    } catch {
      if (itinerarySsoRequestRef.current[normalizedDemandId] !== requestId) {
        return;
      }
      setItinerarySsoByDemandId((current) => ({
        ...current,
        [normalizedDemandId]: { status: "error", editorUrl: "" },
      }));
    }
  }, [itinerarySsoByDemandId]);
  const value = useMemo<CustomizationMobileWorkbenchSessionValue>(
    () => ({
      runtime,
      pendingTraceIds,
      confirmedTraceIds,
      expandedTraceIds,
      itinerarySsoByDemandId,
      buildPagePath,
      confirmTraceSelection() {
        runtime.setConfirmedTraceIds(pendingTraceIds);
        navigate(buildPagePath("workbench"));
      },
      resetPendingTraceSelection() {
        setPendingTraceIds(confirmedTraceIds);
      },
      resolveItinerarySso,
      toggleExpandedTrace(traceId) {
        setExpandedTraceIds((current) =>
          current.includes(traceId)
            ? current.filter((id) => id !== traceId)
            : [...current, traceId],
        );
      },
      togglePendingTrace(traceId) {
        setPendingTraceIds((current) =>
          current.includes(traceId)
            ? current.filter((id) => id !== traceId)
            : [...current, traceId],
        );
      },
    }),
    [
      confirmedTraceIds,
      expandedTraceIds,
      itinerarySsoByDemandId,
      location.search,
      navigate,
      pendingTraceIds,
      resolveItinerarySso,
      routeProjectId,
      runtime,
    ],
  );

  return (
    <CustomizationMobileWorkbenchSessionContext.Provider value={value}>
      <div ref={shellRef} className="customization-mobile-shell">
        <header
          className="customization-mobile-app-header"
          aria-label="手机定制工作台标题栏"
        >
          <Link
            className="customization-mobile-icon-button"
            to={currentPage === "workbench" ? workbenchBackPath : buildPagePath("workbench")}
            aria-label={currentPage === "workbench" ? workbenchBackLabel : "返回手机工作台"}
            onClick={() => {
              if (currentPage === "traces") {
                setPendingTraceIds(confirmedTraceIds);
              }
            }}
          >
            <ArrowLeft size={19} />
          </Link>
          <div className="customization-mobile-app-title">{currentPageTitle}</div>
          <button
            className="customization-mobile-icon-button"
            type="button"
            aria-label="查看当前状态"
            onClick={() => {
              Message.info(`${currentPageTitle}：切换页面会保留当前草稿和浏览位置。`);
            }}
          >
            <CircleHelp size={19} />
          </button>
        </header>
        <nav className="customization-mobile-navigation" aria-label="手机定制工作台导航" role="tablist">
          <MobileNavigationLink
            currentPage={currentPage}
            icon={<ClipboardList size={17} />}
            label="需求定制"
            page="workbench"
            to={buildPagePath("workbench")}
          />
          <MobileNavigationLink
            badge={runtime.sharedRuntime.traces.length}
            currentPage={currentPage}
            icon={<History size={17} />}
            label="需求追溯"
            page="traces"
            to={buildPagePath("traces")}
            ariaLabel="追溯记录"
          />
          <MobileNavigationLink
            currentPage={currentPage}
            icon={<Route size={17} />}
            label="行程计划"
            page="itinerary"
            to={buildPagePath("itinerary")}
          />
        </nav>
        <Outlet />
      </div>
    </CustomizationMobileWorkbenchSessionContext.Provider>
  );
}

const mobileWorkbenchPageTitles: Record<MobileWorkbenchPageKey, string> = {
  itinerary: "行程计划",
  traces: "需求追溯",
  workbench: "需求定制",
};

/** 用配置基址收敛后端返回的单点地址，iframe 不接收任意 origin。 */
function buildTrustedLegacyItineraryUrl(jumpUrl: string): string {
  const baseUrl = import.meta.env.VITE_CUSTOMIZATION_LEGACY_ITINERARY_BASE_URL
    ?.trim()
    .replace(/\/+$/, "");
  const normalizedJumpUrl = jumpUrl.trim();
  if (!baseUrl || baseUrl === "undefined" || !normalizedJumpUrl) {
    return "";
  }
  try {
    const parsedBaseUrl = new URL(baseUrl);
    const parsedJumpUrl = new URL(normalizedJumpUrl, parsedBaseUrl);
    return `${parsedBaseUrl.origin}${parsedJumpUrl.pathname}${parsedJumpUrl.search}${parsedJumpUrl.hash}`;
  } catch {
    return "";
  }
}

/** 读取当前手机工作台页面。 */
function readMobileWorkbenchPageKey(pathname: string): MobileWorkbenchPageKey {
  if (pathname.endsWith("/traces")) {
    return "traces";
  }
  if (pathname.endsWith("/itinerary")) {
    return "itinerary";
  }
  return "workbench";
}

/** 手机工作台顶部页签。 */
function MobileNavigationLink({
  ariaLabel,
  badge,
  currentPage,
  icon,
  label,
  page,
  to,
}: {
  ariaLabel?: string;
  badge?: number;
  currentPage: MobileWorkbenchPageKey;
  icon: ReactNode;
  label: string;
  page: MobileWorkbenchPageKey;
  to: string;
}) {
  const selected = currentPage === page;
  return (
    <Link
      className={`customization-mobile-navigation-link${selected ? " is-selected" : ""}`}
      to={to}
      aria-label={ariaLabel}
      aria-selected={selected}
      role="tab"
    >
      {icon}
      <span>{label}</span>
      {badge !== undefined ? <span className="customization-mobile-navigation-count">{badge}</span> : null}
    </Link>
  );
}

/** 获取当前手机工作台共享会话。 */
export function useCustomizationMobileWorkbenchSession() {
  const session = useContext(CustomizationMobileWorkbenchSessionContext);
  if (!session) {
    throw new Error("Customization mobile workbench pages must render inside the shared shell.");
  }
  return session;
}
